import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import LocalizedText from '@LocalizedText';
import useIntlMessage from '@app/hooks/use-intl-message';
import useFocusTrap from '@app/hooks/use-focus-trap';
import './Dropdown.scss';

const Dropdown = ({ options, onSelect, selected, placeholder, ariaLabelLocaleKey }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownHeaderRef = useRef(null);
  const dropdownOptionsRef = useRef(null);
  const [dropdownAriaLabel] = useIntlMessage(ariaLabelLocaleKey || 'qau-empty-text');

  const closeDropdown = () => {
    setIsOpen(false);
    dropdownHeaderRef.current?.focus();
  };

  const handleSelect = option => {
    setIsOpen(false);
    onSelect(option);
    dropdownHeaderRef.current?.focus();
  };

  useFocusTrap(dropdownOptionsRef, isOpen, {
    triggerRef: dropdownHeaderRef,
    onEscape: closeDropdown,
    focusSelector: '.dropdown__option.selected',
  });

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getOptionClassName = ({ value }) =>
    classnames('dropdown__option', {
      selected: selected && selected.value === value,
    });

  return (
    <div className="dropdown" ref={dropdownRef}>
      <div
        ref={dropdownHeaderRef}
        tabIndex={0}
        role="combobox"
        className="dropdown__header"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setIsOpen(!isOpen)}
        aria-label={dropdownAriaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="dropdown-options"
      >
        {selected?.icon && <img className="dropdown__icon" src={selected.icon} alt="" data-testid="dropdown-icon" />}
        <LocalizedText id={selected ? 'qau-dropdown-option-' + selected.value : placeholder || 'qau-dropdown-default-text'} />
        <span className="dropdown__caret" aria-hidden="true">
          {isOpen ? '▲' : '▼'}
        </span>
      </div>
      {isOpen && (
        <div ref={dropdownOptionsRef} className="dropdown__options" id="dropdown-options" role="listbox">
          {options.map(option => (
            <div
              key={option.value}
              className={getOptionClassName(option)}
              onClick={() => handleSelect(option)}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleSelect(option)}
              role="button"
              aria-pressed={selected?.value === option.value}
              tabIndex={0}
            >
              {option.icon && <img className="dropdown__icon" src={option.icon} alt="" />}
              <LocalizedText id={'qau-dropdown-option-' + option.value} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

Dropdown.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  ariaLabelLocaleKey: PropTypes.string,
  selected: PropTypes.object,
};

export default Dropdown;
